import { useState } from 'react'
import type { AgentCatalogSummary, AuthoringCatalog } from '../../lib/api'
import { AccountPanel } from './AccountPanel'
import { AgentPromptPanel, LogPanel, ScreenshotPanel, WindowsStatePanel } from './CapabilityPanels'
import { DetectionRuleEditor } from './DetectionRuleEditor'
import { DictionaryBindings } from './DictionaryBindings'
import { EnforcementPanel } from './EnforcementPanel'
import { ViolationMessagePicker } from './ViolationMessagePicker'
import {
  DEFAULT_RETRY,
  type JsonObject,
  type PolicyDraft,
  type TriggerKind,
  describeSeconds,
  readAccount,
  readDetectionRules,
  readEnforcement,
  readRedactMatchedText,
  writeDetectionRules
} from './policyDocument'

interface Props {
  draft: PolicyDraft
  catalog: AuthoringCatalog
  agents: AgentCatalogSummary[]
  /** Null until the draft has been saved; bindings chosen before then are held by the page. */
  policyId: string | null
  /** Dictionaries chosen for an unsaved draft, bound by the page on its first save. */
  pendingBindings: string[]
  onPendingBindingsChange: (ids: string[]) => void
  /** How many dictionaries are bound or chosen, which the page's advisories need. */
  onBoundCountChange: (count: number) => void
  /** The violation message chosen for an unsaved draft, used by the page on its first save. */
  pendingViolationMessage: string | null
  onPendingViolationMessageChange: (violationMessageId: string | null) => void
  onChange: (draft: PolicyDraft) => void
}

const AGENT_PROMPT_OBSERVE = 'agent-prompt-observe'

/** The expression that makes an observation policy send only what it found. */
const MATCHED_ONLY = 'event.matched == true'

const PUBLISH_CONDITIONS: [string, string][] = [
  [MATCHED_ONLY, 'A detection rule matched'],
  ["event.highestSeverity == 'critical'", 'A critical rule matched'],
  ['event.blocked == true', 'The prompt was blocked']
]

/**
 * The sentinel for "an expression of my own". It cannot collide with a preset, because a preset is an
 * expression and no expression in the agent's grammar is a bare underscored word.
 */
const CUSTOM_CONDITION = '__custom__'

const TRIGGERS: [TriggerKind, string][] = [
  ['startup', 'When Coastline starts'],
  ['interval', 'On an interval'],
  ['daily', 'Every day at a time'],
  ['event', 'After another policy raises an event']
]

const INTERVAL_UNITS: [string, number][] = [
  ['seconds', 1],
  ['minutes', 60],
  ['hours', 3600],
  ['days', 86400]
]

/** Picks the largest unit that divides the interval exactly, so 300 reads as 5 minutes. */
function splitInterval(seconds: number): [number, number] {
  for (const [, size] of [...INTERVAL_UNITS].reverse())
    if (seconds >= size && seconds % size === 0) return [seconds / size, size]
  return [seconds, 1]
}

/**
 * The policy, edited as a form.
 *
 * Numbered sections in the order the questions are actually asked — what is this, what does it
 * do, what does it look for, what does it do on a match, when does it run, what happens to the
 * result. That order is taken from the agent's own authoring window, and it is the reason the form
 * reads as a sentence rather than as a JSON schema with labels on it.
 *
 * Nothing here validates. Every finding on the page comes from the server's validator, which is the
 * same one the save path and the assistant go through: a second set of rules in the browser would
 * disagree with it eventually, and the disagreement would be invisible until a policy that the editor
 * called valid was rejected by every device in the fleet.
 */
export function GuidedPolicyEditor({
  draft,
  catalog,
  agents,
  policyId,
  pendingBindings,
  onPendingBindingsChange,
  onBoundCountChange,
  pendingViolationMessage,
  onPendingViolationMessageChange,
  onChange
}: Props) {
  const [messageChosen, setMessageChosen] = useState(false)
  const set = (changes: Partial<PolicyDraft>) => onChange({ ...draft, ...changes })
  const detectionRules = readDetectionRules(draft.parameters)
  const account = readAccount(draft.parameters)
  const accountInUse = account.captureTier || account.captureDisplayName

  return (
    <div className="guided-editor" data-testid="guided-editor">
      <section className="card">
        <div className="card-body">
          <h3>1. What this policy is</h3>

          <div className="field">
            <label htmlFor="policy-name">Name</label>
            <input
              id="policy-name"
              value={draft.name}
              maxLength={catalog.limits.maximumNameLength}
              onChange={(event) => set({ name: event.target.value })}
              placeholder="Watch ChatGPT for sensitive prompts"
              data-testid="policy-name"
            />
          </div>

          <div className="field">
            <label htmlFor="policy-description">Description</label>
            <textarea
              id="policy-description"
              className="short"
              value={draft.description}
              maxLength={catalog.limits.maximumDescriptionLength}
              onChange={(event) => set({ description: event.target.value })}
              placeholder="Why this exists, for whoever inherits it."
              data-testid="policy-description"
            />
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card-body">
          <h3>2. What it does</h3>

          <div className="field">
            <label htmlFor="policy-capability">Capability</label>
            <select
              id="policy-capability"
              value={draft.capability}
              onChange={(event) => {
                const capability = event.target.value
                // Parameters belong to a capability, so a change of capability starts a fresh block
                // rather than carrying window selectors into a log policy where nothing reads them.
                onChange({
                  ...draft,
                  capability,
                  parameters: startingParameters(capability, agents),
                  publish: capability === AGENT_PROMPT_OBSERVE ? false : draft.publish,
                  // A prompt watcher left on "when Coastline starts" looks once, at sign-in, at an empty
                  // prompt box. The builder and every bundled template poll at the Release floor, so a
                  // watcher started by hand does too, unless somebody already chose a schedule.
                  trigger:
                    capability === AGENT_PROMPT_OBSERVE && draft.trigger.kind === 'startup'
                      ? { kind: 'interval', seconds: catalog.limits.releasePollSeconds }
                      : draft.trigger
                })
              }}
              data-testid="policy-capability"
            >
              {catalog.capabilities.map((entry) => (
                <option key={entry.name} value={entry.name}>
                  {entry.displayName}
                </option>
              ))}
              {!catalog.capabilities.some((entry) => entry.name === draft.capability) && (
                <option value={draft.capability}>{draft.capability} (not known to this server)</option>
              )}
            </select>
          </div>

          {draft.capability === 'log' && (
            <LogPanel parameters={draft.parameters} onChange={(parameters) => set({ parameters })} />
          )}
          {draft.capability === 'windows-state' && (
            <WindowsStatePanel parameters={draft.parameters} onChange={(parameters) => set({ parameters })} />
          )}
          {draft.capability === 'screenshot' && (
            <ScreenshotPanel parameters={draft.parameters} onChange={(parameters) => set({ parameters })} />
          )}
          {draft.capability === AGENT_PROMPT_OBSERVE && (
            <AgentPromptPanel
              parameters={draft.parameters}
              agents={agents}
              onChange={(parameters) => set({ parameters })}
            />
          )}
        </div>
      </section>

      {draft.capability === AGENT_PROMPT_OBSERVE && (
        <section className="card">
          <div className="card-body">
            <h3>3. What it looks for</h3>

            <DictionaryBindings
              policyId={policyId}
              pending={pendingBindings}
              onPendingChange={onPendingBindingsChange}
              onBoundCountChange={onBoundCountChange}
              maximumContentRules={catalog.limits.maximumContentRules}
              maximumSignalRules={catalog.limits.maximumSignalRules}
              inlineContentRules={detectionRules.filter((rule) => rule.kind === 'content').length}
              inlineSignalRules={detectionRules.filter((rule) => rule.kind === 'signal').length}
            />

            {/* Which account, and on what plan. It sits under the dictionaries rather than beside the
                window selectors because it answers the same question they do -- what is this policy
                looking for -- and because the interesting policy is the one that wants both: a
                personal plan and a dictionary term in the same prompt. */}
            <details className="subsection" open={accountInUse} data-testid="account-section">
              <summary>Which account is signed in{accountInUse && ', and its plan'}</summary>
              <AccountPanel
                parameters={draft.parameters}
                severities={catalog.severities}
                onChange={(parameters) => set({ parameters })}
              />
            </details>

            {/* Folded, and second, because binding a dictionary is the answer nearly every time. A
                rule written into the document belongs to this policy alone: nobody else can correct
                its terms, and correcting them here reaches no other policy that needs the same fix.
                It stays because a template starts an operator here, and a policy that arrived
                carrying rules must be something they can see and take out. */}
            <details className="subsection" open={detectionRules.length > 0} data-testid="inline-rules">
              <summary>
                Rules written into this policy itself
                {detectionRules.length > 0 && ` (${detectionRules.length})`}
              </summary>
              <p className="hint">
                These travel inside the document rather than being merged from a dictionary, so they
                are this policy's alone. Prefer a dictionary for anything more than one policy will
                ever want.
              </p>
              <DetectionRuleEditor
                rules={detectionRules}
                redactMatchedText={readRedactMatchedText(draft.parameters)}
                catalog={catalog}
                onChange={(rules, redact) => set({ parameters: writeDetectionRules(draft.parameters, rules, redact) })}
              />
            </details>
          </div>
        </section>
      )}

      {draft.capability === AGENT_PROMPT_OBSERVE && (
        <section className="card">
          <div className="card-body">
            <h3>4. When a rule matches</h3>
            <EnforcementPanel
              parameters={draft.parameters}
              severities={catalog.severities}
              onChange={(parameters) => set({ parameters })}
              messageChosen={messageChosen}
              messagePicker={
                <ViolationMessagePicker
                  policyId={policyId}
                  pending={pendingViolationMessage}
                  onPendingChange={onPendingViolationMessageChange}
                  notifies={readEnforcement(draft.parameters).mode === 'act' && readEnforcement(draft.parameters).notifyUser}
                  onChosenChange={setMessageChosen}
                />
              }
            />
          </div>
        </section>
      )}

      <section className="card">
        <div className="card-body">
          <h3>{draft.capability === AGENT_PROMPT_OBSERVE ? '5' : '3'}. When it runs</h3>

          <div className="field">
            <label htmlFor="trigger-kind">Trigger</label>
            <select
              id="trigger-kind"
              value={draft.trigger.kind}
              onChange={(event) => set({ trigger: startingTrigger(event.target.value as TriggerKind) })}
              data-testid="trigger-kind"
            >
              {TRIGGERS.map(([kind, label]) => (
                <option key={kind} value={kind}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {draft.trigger.kind === 'interval' && (
            <IntervalField
              key={draft.id ?? 'new'}
              seconds={draft.trigger.seconds ?? 300}
              releasePollSeconds={catalog.limits.releasePollSeconds}
              onChange={(seconds) => set({ trigger: { kind: 'interval', seconds } })}
            />
          )}

          {draft.trigger.kind === 'daily' && (
            <>
              <div className="field">
                <label htmlFor="daily-time">Local time</label>
                <input
                  id="daily-time"
                  type="time"
                  value={draft.trigger.localTime ?? '09:00'}
                  onChange={(event) => set({ trigger: { ...draft.trigger, localTime: event.target.value } })}
                  data-testid="daily-time"
                />
              </div>
              <div className="field">
                <label htmlFor="daily-zone">Time zone</label>
                <input
                  id="daily-zone"
                  value={draft.trigger.timeZoneId ?? ''}
                  onChange={(event) =>
                    set({ trigger: { ...draft.trigger, timeZoneId: event.target.value || undefined } })
                  }
                  placeholder="Leave empty for the device's own time zone"
                  data-testid="daily-zone"
                />
                <div className="hint">
                  A Windows or IANA identifier, such as Pacific Standard Time or Europe/London. Empty
                  means each device fires at that time where it is, which is usually what is wanted.
                </div>
              </div>
            </>
          )}

          {draft.trigger.kind === 'event' && (
            <>
              <div className="field">
                <label htmlFor="event-name">Event</label>
                <select
                  id="event-name"
                  value={draft.trigger.name ?? ''}
                  onChange={(event) => set({ trigger: { kind: 'event', name: event.target.value } })}
                  data-testid="event-name"
                >
                  <option value="">Choose an event…</option>
                  {catalog.eventNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                  {draft.trigger.name && !catalog.eventNames.includes(draft.trigger.name) && (
                    <option value={draft.trigger.name}>{draft.trigger.name}</option>
                  )}
                </select>
              </div>

              <div className="field">
                <label htmlFor="condition-preset">Only when</label>
                <select
                  id="condition-preset"
                  value={
                    catalog.conditionPresets.some((preset) => preset.expression === draft.condition)
                      ? draft.condition
                      : CUSTOM_CONDITION
                  }
                  onChange={(event) => {
                    // Choosing "an expression of my own" leaves the box alone rather than clearing it:
                    // the operator is almost always about to edit the preset they just picked.
                    if (event.target.value !== CUSTOM_CONDITION) set({ condition: event.target.value })
                  }}
                  data-testid="condition-preset"
                >
                  {catalog.conditionPresets.map((preset) => (
                    <option key={preset.label} value={preset.expression}>
                      {preset.label}
                    </option>
                  ))}
                  <option value={CUSTOM_CONDITION}>An expression of my own</option>
                </select>
              </div>

              <div className="field">
                <label htmlFor="condition-expression">Condition</label>
                <input
                  id="condition-expression"
                  className="mono"
                  value={draft.condition}
                  onChange={(event) => set({ condition: event.target.value })}
                  placeholder="event.matched == true"
                  data-testid="condition-expression"
                />
                <div className="hint">
                  Available fields: event.matched, event.matchCount, event.matchedRuleNames,
                  event.highestSeverity, event.signalSummary, event.agentId. Approved functions:{' '}
                  {catalog.approvedConditionFunctions.join(' and ')}.
                </div>
                <div className="hint">
                  The agent checks a condition against an empty context when it imports the policy, so a
                  greater-than or less-than comparison against one of these fields is rejected outright.
                  Use == and != instead.
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-body">
          <h3>{draft.capability === AGENT_PROMPT_OBSERVE ? '6' : '4'}. What happens with the result</h3>

          <label className="inline-check">
            <input
              type="checkbox"
              checked={draft.publish}
              onChange={(event) =>
                set({
                  publish: event.target.checked,
                  // Turning publishing on for prompt observation narrows it in the same act. Publishing
                  // every prompt on every interval is a real choice and it is the next control down,
                  // but it must not be what an operator gets for ticking one box: the safe default is
                  // the one that has to be turned off, not the one that has to be found.
                  //
                  // A condition without publish is refused by both sides, so it goes when publishing
                  // does rather than being left to fail validation later.
                  publishCondition: !event.target.checked
                    ? ''
                    : draft.capability === AGENT_PROMPT_OBSERVE && draft.publishCondition.trim() === ''
                      ? MATCHED_ONLY
                      : draft.publishCondition
                })
              }
              data-testid="policy-publish"
            />
            <span>Send the result to the collector</span>
          </label>
          <div className="hint">
            {draft.publish
              ? 'The result is recorded on the device and sent here, where it is normalized, queryable, and exportable.'
              : 'The result stays on the device. Nothing about this policy will appear in Explore or in an alert.'}
          </div>

          {draft.publish && (
            <>
              <label className="inline-check" style={{ marginTop: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={draft.publishCondition.trim() === ''}
                  onChange={(event) =>
                    set({ publishCondition: event.target.checked ? '' : MATCHED_ONLY })
                  }
                  data-testid="policy-publish-everything"
                />
                <span>Send every result, including the ones that found nothing</span>
              </label>
              <div className="hint">
                {draft.capability === AGENT_PROMPT_OBSERVE ? (
                  draft.publishCondition.trim() === '' ? (
                    <>
                      <strong>This sends the text of every prompt observed, on every interval,</strong>{' '}
                      whether or not it matched a rule. On a one-minute interval that is well over a
                      thousand bodies per device per day. Prompt text is stored here byte-exact and is
                      not removed by changing this policy later.
                    </>
                  ) : (
                    'Only observations that matched a bound dictionary rule are sent. Everything else stays on the device and never leaves it.'
                  )
                ) : draft.publishCondition.trim() === '' ? (
                  'Every result this policy produces is sent.'
                ) : (
                  'Only results that satisfy the condition below are sent.'
                )}
              </div>

              {draft.publishCondition.trim() !== '' && (
                <div className="field" style={{ marginTop: '0.85rem' }}>
                  <label htmlFor="publish-condition">Send only when</label>
                  <select
                    id="publish-condition"
                    value={
                      PUBLISH_CONDITIONS.some(([expression]) => expression === draft.publishCondition)
                        ? draft.publishCondition
                        : CUSTOM_CONDITION
                    }
                    onChange={(event) =>
                      set({
                        publishCondition: event.target.value === CUSTOM_CONDITION ? draft.publishCondition : event.target.value
                      })
                    }
                    data-testid="publish-condition-preset"
                  >
                    {PUBLISH_CONDITIONS.map(([expression, label]) => (
                      <option key={expression} value={expression}>
                        {label}
                      </option>
                    ))}
                    <option value={CUSTOM_CONDITION}>An expression of my own</option>
                  </select>
                  {!PUBLISH_CONDITIONS.some(([expression]) => expression === draft.publishCondition) && (
                    <input
                      value={draft.publishCondition}
                      onChange={(event) => set({ publishCondition: event.target.value })}
                      data-testid="publish-condition"
                      style={{ marginTop: '0.5rem' }}
                    />
                  )}
                  <div className="hint">
                    Evaluated on the device against the result. The agent checks it against an empty
                    context first, so compare with <code>==</code> rather than with <code>&gt;</code>:
                    <code> event.matchCount &gt; 0</code> is refused where{' '}
                    <code>event.matched == true</code> is accepted.
                  </div>
                </div>
              )}
            </>
          )}

          <label className="inline-check" style={{ marginTop: '0.85rem' }}>
            <input
              type="checkbox"
              checked={draft.retry !== null}
              onChange={(event) => set({ retry: event.target.checked ? { ...DEFAULT_RETRY } : null })}
              data-testid="policy-retry"
            />
            <span>Retry when it fails</span>
          </label>

          {draft.retry && (
            <div className="number-grid" style={{ marginTop: '0.6rem' }}>
              <div className="field">
                <label htmlFor="retry-attempts">Attempts</label>
                <input
                  id="retry-attempts"
                  type="number"
                  min={1}
                  max={catalog.limits.maximumRetryAttempts}
                  value={draft.retry.maximumRetries}
                  onChange={(event) =>
                    set({
                      retry: {
                        ...draft.retry!,
                        maximumRetries: Number.parseInt(event.target.value, 10) || 1
                      }
                    })
                  }
                  data-testid="retry-attempts"
                />
              </div>
              <div className="field">
                <label htmlFor="retry-initial">First wait (s)</label>
                <input
                  id="retry-initial"
                  type="number"
                  min={1}
                  value={draft.retry.initialSeconds}
                  onChange={(event) =>
                    set({ retry: { ...draft.retry!, initialSeconds: Number.parseFloat(event.target.value) || 1 } })
                  }
                  data-testid="retry-initial"
                />
              </div>
              <div className="field">
                <label htmlFor="retry-maximum">Longest wait (s)</label>
                <input
                  id="retry-maximum"
                  type="number"
                  min={1}
                  value={draft.retry.maximumSeconds}
                  onChange={(event) =>
                    set({ retry: { ...draft.retry!, maximumSeconds: Number.parseFloat(event.target.value) || 1 } })
                  }
                  data-testid="retry-maximum"
                />
              </div>
            </div>
          )}
        </div>
      </section>

    </div>
  )
}

/**
 * An interval, as a number and a unit.
 *
 * The unit is held rather than derived from the stored seconds. Deriving it looks equivalent and is
 * not: typing "120" into a seconds field would land on 120, which divides by sixty, and the control
 * under the cursor would silently become "2 minutes" mid-keystroke. Re-deriving on mount is enough,
 * and the key on this element is the policy id, so opening a different policy re-reads it.
 */
function IntervalField({
  seconds,
  releasePollSeconds,
  onChange
}: {
  seconds: number
  releasePollSeconds: number
  onChange: (seconds: number) => void
}) {
  const [unit, setUnit] = useState(() => splitInterval(seconds)[1])
  const value = Math.max(1, Math.round(seconds / unit))

  return (
    <>
      <div className="field inline-fields">
        <div>
          <label htmlFor="interval-value">Run every</label>
          <input
            id="interval-value"
            type="number"
            min={1}
            value={value}
            onChange={(event) => onChange(Math.max(1, Number.parseInt(event.target.value, 10) || 1) * unit)}
            data-testid="interval-value"
          />
        </div>
        <div>
          <label htmlFor="interval-unit">Unit</label>
          <select
            id="interval-unit"
            value={unit}
            onChange={(event) => {
              const next = Number(event.target.value)
              setUnit(next)
              onChange(value * next)
            }}
            data-testid="interval-unit"
          >
            {INTERVAL_UNITS.map(([label, size]) => (
              <option key={label} value={size}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="hint">
        Runs every {describeSeconds(seconds)}. A released agent polls every {releasePollSeconds} seconds,
        so anything shorter than that runs no more often than that.
      </div>
    </>
  )
}

function startingTrigger(kind: TriggerKind) {
  switch (kind) {
    case 'interval':
      return { kind, seconds: 300 } as const
    case 'daily':
      return { kind, localTime: '09:00' } as const
    case 'event':
      return { kind, name: '' } as const
    default:
      return { kind: 'startup' } as const
  }
}

function startingParameters(capability: string, agents: AgentCatalogSummary[]) {
  switch (capability) {
    case 'windows-state':
      return { maximumWindows: 500 }
    case 'screenshot':
      return { target: 0 }
    case AGENT_PROMPT_OBSERVE: {
      const first = agents[0]
      return first ? ({ ...first.parameters } as JsonObject) : {}
    }
    default:
      return { message: 'Coastline is running.' }
  }
}
